import React, { useCallback, useEffect, useState } from "react";
import {
  getPendingCount,
  getAttentionDrafts,
  getAllDrafts,
  clearAllDrafts,
} from "../lib/offlineDb";
import {
  triggerSync,
  retryDraft,
  CHANGE_EVENT,
} from "../lib/inspectionSyncEngine";

// Friendly labels for the per-type queue breakdown.
const LABELS = {
  monthly: "Monthly inspections",
  initial: "Initial inspections",
  propertyPhotos: "Property photos",
  ticketDocument: "Ticket documents",
  ticketNote: "Ticket notes",
  ticketForm: "Tickets",
  report: "Reports",
};

/**
 * PendingSyncBadge — global, always-mounted indicator for offline uploads.
 *
 * Owns the app-wide sync bootstrap: it kicks a sync on boot and whenever the
 * browser fires `online`, and reflects how many reports are still queued. When a
 * report needs attention (validation rejected, or attempts exhausted) it exposes
 * a per-item Retry. Renders nothing when the queue is empty.
 */
const PendingSyncBadge = () => {
  const [pending, setPending] = useState(0);
  const [attention, setAttention] = useState([]);
  const [breakdown, setBreakdown] = useState({});
  const [open, setOpen] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [count, items, all] = await Promise.all([
        getPendingCount(),
        getAttentionDrafts(),
        getAllDrafts(),
      ]);
      setPending(count);
      setAttention(items);
      // Count queued drafts by type so the user can see what's backed up.
      const counts = {};
      all
        .filter((d) => d.status !== "draft")
        .forEach((d) => {
          const k = d.reportType || "report";
          counts[k] = (counts[k] || 0) + 1;
        });
      setBreakdown(counts);
    } catch (_) {
      // Offline storage unavailable — nothing to show.
    }
  }, []);

  const handleClearAll = useCallback(async () => {
    if (
      !window.confirm(
        "Discard ALL pending offline uploads in the queue? This cannot be undone."
      )
    ) {
      return;
    }
    try {
      await clearAllDrafts();
    } catch (_) {
      /* no-op */
    }
    refresh();
  }, [refresh]);

  useEffect(() => {
    refresh();
    triggerSync(); // flush anything left over from a previous session

    const onChange = () => refresh();
    const onOnline = () => triggerSync();
    window.addEventListener(CHANGE_EVENT, onChange);
    window.addEventListener("online", onOnline);
    return () => {
      window.removeEventListener(CHANGE_EVENT, onChange);
      window.removeEventListener("online", onOnline);
    };
  }, [refresh]);

  if (pending === 0) return null;

  const hasAttention = attention.length > 0;

  return (
    <div style={styles.wrap}>
      {open && (
        <div style={styles.panel}>
          <div style={styles.panelHeader}>
            Pending uploads ({pending})
            <button style={styles.close} onClick={() => setOpen(false)}>
              ×
            </button>
          </div>
          {Object.keys(breakdown).length > 0 && (
            <div style={{ ...styles.row, fontSize: 12, color: "#6b7c93" }}>
              {Object.entries(breakdown)
                .map(([k, n]) => `${LABELS[k] || k}: ${n}`)
                .join(" · ")}
            </div>
          )}
          {!hasAttention && (
            <div style={styles.row}>
              Uploading in the background… they’ll sync automatically.
            </div>
          )}
          {attention.map((d) => (
            <div key={d.id} style={styles.row}>
              <div style={{ fontWeight: 600 }}>
                Property {d.propertyId} — needs attention
              </div>
              <div style={styles.err}>{d.lastError || "Upload failed"}</div>
              <button
                style={styles.retryBtn}
                onClick={async () => {
                  await retryDraft(d.id);
                  refresh();
                }}
              >
                Retry
              </button>
            </div>
          ))}
          <button
            style={styles.retryAll}
            onClick={() => triggerSync()}
          >
            Retry all now
          </button>
          <button style={styles.clearAll} onClick={handleClearAll}>
            Clear all pending
          </button>
        </div>
      )}
      <button
        style={{
          ...styles.pill,
          background: hasAttention ? "#b94a48" : "#2f7ed8",
        }}
        onClick={() => setOpen((o) => !o)}
        title="Offline uploads pending"
      >
        {hasAttention ? "⚠" : "⏳"} {pending} pending upload
        {pending > 1 ? "s" : ""}
      </button>
    </div>
  );
};

const styles = {
  wrap: {
    position: "fixed",
    right: 16,
    bottom: 16,
    zIndex: 9999,
    fontSize: 13,
  },
  pill: {
    color: "#fff",
    border: "none",
    borderRadius: 20,
    padding: "8px 14px",
    cursor: "pointer",
    boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
  },
  panel: {
    background: "#fff",
    border: "1px solid #ddd",
    borderRadius: 8,
    boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
    width: 280,
    marginBottom: 8,
    overflow: "hidden",
  },
  panelHeader: {
    background: "#f5f5f5",
    padding: "8px 12px",
    fontWeight: 600,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  close: {
    border: "none",
    background: "none",
    fontSize: 18,
    cursor: "pointer",
    lineHeight: 1,
  },
  row: { padding: "8px 12px", borderTop: "1px solid #eee" },
  err: { color: "#b94a48", fontSize: 12, margin: "4px 0" },
  retryBtn: {
    border: "1px solid #2f7ed8",
    background: "#fff",
    color: "#2f7ed8",
    borderRadius: 4,
    padding: "2px 10px",
    cursor: "pointer",
  },
  retryAll: {
    width: "100%",
    border: "none",
    background: "#2f7ed8",
    color: "#fff",
    padding: "8px",
    cursor: "pointer",
  },
  clearAll: {
    width: "100%",
    border: "none",
    background: "#b94a48",
    color: "#fff",
    padding: "8px",
    cursor: "pointer",
    borderTop: "1px solid rgba(255,255,255,0.2)",
  },
};

export default PendingSyncBadge;
